import React from "react";
import WideContainer from "../components/WideContainer";
import CircularProgress from "@mui/material/CircularProgress";
import DoneIcon from "@mui/icons-material/Done";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link } from "react-router-dom";
import MovieIcon from '@mui/icons-material/Movie';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import ButtonGroup from "@mui/material/ButtonGroup";
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { createSvgIcon } from "@mui/material/utils";
import Dropzone from "react-dropzone";
import { Trans, withTranslation } from "react-i18next";

import UserBar from "../components/UserBar";
import axios from "../components/Axios";
import { withCdnCookie } from "../app/cdnCookie";
import { fmt } from "./videos/scenes/sceneModel";
import "./Videos.css";
import "./Library.css";

// the design's view_comfy is the classic 4x3 grid; @mui/icons-material 6 ships a newer one-over-two glyph
const ViewComfyIcon = createSvgIcon(
  <path d="M3 9h4V5H3v4zm0 5h4v-4H3v4zm5 0h4v-4H8v4zm5 0h4v-4h-4v4zM8 9h4V5H8v4zm5-4v4h4V5h-4zm5 9h4v-4h-4v4zM3 19h4v-4H3v4zm5 0h4v-4H8v4zm5 0h4v-4h-4v4zm5 0h4v-4h-4v4zm0-14v4h4V5h-4z" />,
  "ViewComfy"
);

const STATUS = {
  PROCESSED: { label: "Finished", Icon: DoneIcon, cls: "st-done" },
  PROCESSING: { label: "Processing", Icon: RefreshIcon, cls: "st-processing" },
  NOT_PROCESSED: { label: "Not Processed", Icon: ErrorOutlineIcon, cls: "st-idle" },
  ERROR: { label: "Error", Icon: ErrorOutlineIcon, cls: "st-error" },
};

const VIEWS = [
  { view: "list", label: "List", Icon: ViewListIcon },
  { view: "detailed", label: "Detailed", Icon: ViewModuleIcon },
  { view: "thumbnail", label: "Thumbnail", Icon: ViewComfyIcon },
];

// folder paths are "a/b/c"; "" is the library root
const parentOf = (f) => (f.lastIndexOf("/") < 0 ? "" : f.slice(0, f.lastIndexOf("/")));
const baseName = (f) => f.slice(f.lastIndexOf("/") + 1);
const byName = (a, b) => baseName(a).localeCompare(baseName(b), undefined, { numeric: true, sensitivity: "base" });
const folderUrl = (f) => (f ? "/?folder=" + encodeURIComponent(f).replace(/%2F/g, "/") : "/");
const INDENT = 20;

// list sort; `first` is the direction a column starts in when clicked
const SORTS = {
  name: { label: "Name", first: "asc" },
  uploaded: { label: "Uploaded", first: "desc" },
  duration: { label: "Duration", first: "desc" },
  status: { label: "Status", first: "asc" },
};
const DEFAULT_SORT = { key: "uploaded", dir: "desc" };
// ascending status puts videos needing attention first
const STATUS_RANK = { ERROR: 0, PROCESSING: 1, NOT_PROCESSED: 2, PROCESSED: 3 };
const sortValue = {
  uploaded: (v) => Date.parse(v.created_time_z) || null,
  duration: (v) => (v.duration ? v.duration.split(":").reduce((s, p) => s * 60 + (Number(p) || 0), 0) : null),
  status: (v) => STATUS_RANK[v.status] ?? null,
};
const byVideoName = (a, b) => a.project_name.localeCompare(b.project_name, undefined, { numeric: true, sensitivity: "base" });

function sortVideos(list, { key, dir }) {
  let sign = dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    if (key === "name") return sign * byVideoName(a, b);
    let va = sortValue[key](a);
    let vb = sortValue[key](b);
    if (va !== vb) {
      // missing values (no duration yet, unknown status) go last in either direction
      if (va === null) return 1;
      if (vb === null) return -1;
      return sign * (va - vb);
    }
    // ties: newest first, then by name
    return (sortValue.uploaded(b) || 0) - (sortValue.uploaded(a) || 0) || byVideoName(a, b);
  });
}

function loadSort() {
  let [key, dir] = (localStorage.getItem("librarySort") || "").split(":");
  return SORTS[key] && (dir === "asc" || dir === "desc") ? { key, dir } : DEFAULT_SORT;
}

class Index extends React.Component {
  constructor(props) {
    super(props);
    let view = localStorage.getItem("view");
    if (!VIEWS.some((v) => v.view === view)) {
      view = "list";
    }

    this.state = {
      items: [],
      folders: [], // folders as returned by the api
      allFolders: [], // folders plus their ancestors, so nested-only parents show up
      expanded: {}, // folder path -> expanded inline in the list
      sub: {}, // folder path -> videos, fetched on first expand
      folder: null,
      loading: false,
      filter: "",
      view: view,
      sort: loadSort(),
      menuVid: 0,
      menuAnchor: null,
      edit_open: false,
      edit_name: "",
      edit_folder: "",
      edit_video: "",
      folderAnchor: null,
      edit_folder_open: false,
      create_folder_open: false,
      folder_old: "",
      folder_new: "",
    };
    this.user = JSON.parse(localStorage.getItem("user"));
    this.timeout = null;
    this.tvideo = React.createRef();
    this.dlgInput = React.createRef();
    this.dropzoneRef = React.createRef();
    this.onDrop = this.onDrop.bind(this);

    if (this.user === null) {
      this.props.history.push("/login");
      return;
    }
  }

  componentDidMount() {
    this.unmounted = false;
    this.onUploadComplete = () => this.loadData();
    window.addEventListener('upload-complete', this.onUploadComplete);
    this.loadData();
  }

  loadData(quiet) {
    clearTimeout(this.timeout);
    this.timeout = null;
    let u = localStorage.getItem("user_id");
    if (u === null) {
      this.props.history.push("/login");
      return;
    }
    let query = new URLSearchParams(this.props.location.search);

    if (!quiet) this.setState({ loading: true });
    // thumbnails are on the CDN: show them once its cookie is set
    withCdnCookie(
      axios.get("/user/" + u + "/video_projects", {
        params: { folder: query.get("folder") },
      })
    )
      .then(
        function (response) {
          let all = new Set();
          for (let f of response.data.folders) {
            let parts = f.split("/");
            for (let i = 1; i <= parts.length; i++) {
              let p = parts.slice(0, i).join("/");
              if (p !== "") all.add(p);
            }
          }

          this.setState({
            loading: false,
            items: response.data.videos,
            folder: query.get("folder"),
            folders: response.data.folders,
            allFolders: [...all],
          }, () => this.schedulePoll());

          // refresh folders that are open inline, their videos may have changed too
          for (let f in this.state.expanded) {
            if (this.state.expanded[f]) this.fetchFolder(f);
          }
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
          this.setState({ loading: false });
        }.bind(this)
      );
  }

  fetchFolder(f) {
    axios
      .get("/user/" + localStorage.getItem("user_id") + "/video_projects", {
        params: { folder: f },
      })
      .then((response) => {
        this.setState((s) => ({ sub: { ...s.sub, [f]: response.data.videos } }), () => this.schedulePoll());
      })
      .catch((error) => {
        console.log(error);
        this.setState((s) => ({ sub: { ...s.sub, [f]: [] } }));
      });
  }

  // reload every 15s while a video on screen (current folder or one open inline) is still processing
  schedulePoll() {
    if (this.timeout !== null || this.unmounted) return;
    let shown = [...this.state.items];
    for (let f in this.state.expanded) {
      if (this.state.expanded[f]) shown.push(...(this.state.sub[f] || []));
    }
    if (shown.some((v) => v.status === "PROCESSING")) {
      this.timeout = setTimeout(() => this.loadData(true), 15000);
    }
  }

  toggleFolder(f) {
    let open = !this.state.expanded[f];
    this.setState((s) => ({ expanded: { ...s.expanded, [f]: open } }));
    if (open && this.state.sub[f] === undefined) this.fetchFolder(f);
  }

  folderKeyDown(f, e) {
    if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      this.toggleFolder(f);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      this.loadData();
      return;
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.timeout !== null) clearTimeout(this.timeout);
    window.removeEventListener('upload-complete', this.onUploadComplete);
  }

  filterChange(e) {
    this.setState({ filter: e.target.value });
  }

  viewChange(v, e) {
    localStorage.setItem("view", v);
    this.setState({ view: v });
  }

  // clicking the active column flips its direction, another column starts in its natural direction
  sortChange(key) {
    let { sort } = this.state;
    let dir = sort.key === key ? (sort.dir === "asc" ? "desc" : "asc") : SORTS[key].first;
    localStorage.setItem("librarySort", key + ":" + dir);
    this.setState({ sort: { key, dir } });
  }

  handleEdit(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  formatDate(v) {
    if (!v) return "";
    let d = new Date(v);
    let s = d.toDateString().split(" ");
    return (
      <span title={d.toLocaleTimeString("en-US")}>
        {s[1] + " " + s[2] + ", " + s[3]}
      </span>
    );
  }

  // "01:03:15" as 1:03:15, like the video's own pages
  formatDuration(v) {
    if (!v) return "";
    const s = String(v)
      .split(":")
      .reduce((n, p) => n * 60 + (Number(p) || 0), 0);
    return fmt(s);
  }

  videoUrl(v) {
    return v.status === "PROCESSED"
      ? "/project/" + v.project_id + "/overview"
      : "/project_progress/" + v.project_id;
  }

  thumbnail(v) {
    return (
      <img
        src={this.user.user_folder + "/" + v.project_folder + "/video_lowres_thumbnail.jpg"}
        alt=""
        loading="lazy"
        onError={(e) => (e.currentTarget.style.visibility = "hidden")}
      />
    );
  }

  menuClick(i, e) {
    e.stopPropagation();
    e.preventDefault();
    this.setState({
      menuAnchor: e.currentTarget,
      menuVid: i.project_id,
      edit_name: i.project_name,
      edit_folder: i.folder,
      edit_video: i,
    });
  }
  deleteProject() {
    this.setState({ menuAnchor: null });
    axios
      .delete("/video_project/" + this.state.menuVid)
      .then(
        function (response) {
          this.loadData();
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error.response);
          alert(error.response.data.title);
          //this.setState({ loading: false });
        }.bind(this)
      );
  }

  updateProject(e) {
    if (e) e.preventDefault();
    this.setState({ menuAnchor: null, edit_open: false });
    axios
      .post("/video_project/" + this.state.menuVid, {
        project_name: this.state.edit_name,
        folder: this.state.edit_folder,
      })
      .then(
        function (response) {
          this.loadData();
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error.response);
          alert(error.response.data.title);
          //this.setState({ loading: false });
        }.bind(this)
      );
  }
  renameFolder(e) {
    if (e) e.preventDefault();
    this.setState({ folderAnchor: null, edit_folder_open: false });
    axios
      .post("/folder/rename", {
        folder_new: this.state.folder_new,
        folder_old: this.state.folder_old,
      })
      .then(
        function (response) {
          this.loadData();
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error.response);
          alert(error.response.data.title);
          //this.setState({ loading: false });
        }.bind(this)
      );
  }
  createFolder(e) {
    if (e) e.preventDefault();
    this.setState({ folderAnchor: null, create_folder_open: false });
    axios
      .post("/folder/create", {
        folder_new: this.state.folder_new,
      })
      .then(
        function (response) {
          this.loadData();
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error.response);
          alert(error.response.data.title);
          //this.setState({ loading: false });
        }.bind(this)
      );
  }
  deleteFolder() {
    this.setState({ folderAnchor: null });
    axios
      .post("/folder/delete", {
        folder: this.state.folder_old,
      })
      .then(
        function (response) {
          this.loadData();
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error.response);
          alert(error.response.data.title);
          //this.setState({ loading: false });
        }.bind(this)
      );
  }
  childFolders(parent) {
    return this.state.allFolders.filter((f) => parentOf(f) === parent).sort(byName);
  }

  // Flattened list: folders of the current folder (expanded ones followed by their contents), then its videos.
  buildRows() {
    let { expanded, sub, sort } = this.state;
    let q = this.state.filter.toLowerCase();
    let videos = (list) => sortVideos((list || []).filter((v) => v.project_name.toLowerCase().includes(q)), sort);
    let rows = [];
    let walk = (parent, depth) => {
      // folders stay on top, in name order (reversed only when sorting by name descending)
      let folders = this.childFolders(parent);
      if (sort.key === "name" && sort.dir === "desc") folders.reverse();
      for (let f of folders) {
        let open = !!expanded[f];
        rows.push({ type: "folder", path: f, depth, open });
        if (!open) continue;
        let before = rows.length;
        walk(f, depth + 1);
        if (sub[f] === undefined) {
          rows.push({ type: "loading", parent: f, depth: depth + 1 });
          continue;
        }
        for (let v of videos(sub[f])) rows.push({ type: "video", video: v, parent: f, depth: depth + 1 });
        if (rows.length === before) rows.push({ type: "empty", parent: f, depth: depth + 1 });
      }
    };
    let root = this.state.folder || "";
    walk(root, 0);
    for (let v of videos(this.state.items)) rows.push({ type: "video", video: v, parent: root, depth: 0 });
    return rows;
  }

  folderIcon(open) {
    return open ? (
      <FolderOpenIcon className="lib-folderIcon is-open" />
    ) : (
      <FolderIcon className="lib-folderIcon" />
    );
  }

  folderMore(f) {
    return (
      <div className={"lib-more" + (this.state.folderAnchor && this.state.folder_old === f ? " is-open" : "")}>
        {this.state.folders.includes(f) && (
          <IconButton
            className="lib-moreBtn"
            size="small"
            title={this.props.t("More")}
            aria-haspopup="true"
            aria-expanded={this.state.folderAnchor && this.state.folder_old === f ? "true" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              this.setState({
                folderAnchor: e.currentTarget,
                folder_old: f,
                folder_new: f,
              });
            }}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </div>
    );
  }

  renderTree() {
    let { t } = this.props;
    let nodes = [{ path: null, label: t("Library"), depth: 0 }];
    let walk = (parent, depth) => {
      for (let f of this.childFolders(parent)) {
        nodes.push({ path: f, label: baseName(f), depth });
        walk(f, depth + 1);
      }
    };
    walk("", 1);
    let current = this.state.folder || null;

    return (
      <div className="lib-side">
        <div className="lib-sideHead">
          <span className="lib-overline">
            <Trans>Folders</Trans>
          </span>
          <IconButton
            className="lib-brandBtn"
            size="small"
            title={t("New Folder")}
            onClick={() => {
              this.setState({ create_folder_open: true, folder_new: "" });
            }}
          >
            <CreateNewFolderIcon />
          </IconButton>
        </div>
        <nav className="lib-tree">
          {nodes.map((n) => {
            let on = n.path === current;
            let hasKids =
              n.path === null
                ? this.state.allFolders.length > 0
                : this.state.allFolders.some((f) => f.startsWith(n.path + "/"));
            let Icon = n.path === null ? VideoLibraryIcon : FolderIcon;
            return (
              <Link
                key={n.path || ""}
                to={folderUrl(n.path)}
                className={"lib-treeItem noUnderline" + (on ? " is-on" : "")}
                style={{ paddingLeft: 2 + n.depth * 14 }}
                aria-current={on ? "page" : undefined}
                title={n.label}
              >
                <span className="lib-treeCaret">{hasKids && <ExpandMoreIcon />}</span>
                <Icon className="lib-treeIcon" />
                <span className="lib-treeLabel">{n.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  renderListRow(r) {
    let { t } = this.props;
    let pad = { paddingLeft: 12 + r.depth * INDENT };

    if (r.type === "folder") {
      return (
        <div
          key={"f:" + r.path}
          className="lib-row is-clickable"
          style={pad}
          role="button"
          tabIndex={0}
          aria-expanded={r.open}
          onClick={this.toggleFolder.bind(this, r.path)}
          onKeyDown={this.folderKeyDown.bind(this, r.path)}
        >
          <div className="lib-slot">
            {r.open ? <ExpandMoreIcon className="lib-caret" /> : <NavigateNextIcon className="lib-caret" />}
            {this.folderIcon(r.open)}
          </div>
          <div className="lib-label lib-folderName" title={r.path}>
            {baseName(r.path)}
          </div>
          {this.folderMore(r.path)}
        </div>
      );
    }

    if (r.type === "video") {
      let v = r.video;
      let st = STATUS[v.status] || STATUS.PROCESSING;
      let done = v.status === "PROCESSED";
      return (
        <Link key={"v:" + v.project_id} to={this.videoUrl(v)} className="lib-row noUnderline" style={pad}>
          <div className="lib-slot">
            {done ? (
              <div className="lib-thumb">{this.thumbnail(v)}</div>
            ) : (
              <div className="lib-thumb is-empty">
                <MovieIcon />
              </div>
            )}
          </div>
          <div className="lib-label lib-videoName" title={v.project_name}>
            {v.project_name}
          </div>
          <div className="lib-date">{this.formatDate(v.created_time_z)}</div>
          <div className="lib-dur">{this.formatDuration(v.duration)}</div>
          <div className={"lib-status " + st.cls} title={t(st.label)}>
            <st.Icon />
            {!done && <span>{t(st.label)}</span>}
          </div>
          <div className={"lib-more" + (this.state.menuAnchor && this.state.menuVid === v.project_id ? " is-open" : "")}>
            <IconButton
              className="lib-moreBtn"
              size="small"
              title={t("More")}
              aria-haspopup="true"
              aria-expanded={this.state.menuAnchor && this.state.menuVid === v.project_id ? "true" : undefined}
              onClick={this.menuClick.bind(this, v)}
            >
              <MoreVertIcon />
            </IconButton>
          </div>
        </Link>
      );
    }

    return (
      <div key={r.type + ":" + r.parent} className="lib-row" style={pad}>
        <div className="lib-slot">{r.type === "loading" && <CircularProgress size={14} />}</div>
        {r.type === "empty" && <div className="lib-note">{t("No videos")}</div>}
      </div>
    );
  }

  sortHeader(key, className) {
    let { sort } = this.state;
    let on = sort.key === key;
    let Arrow = sort.dir === "asc" ? ArrowUpwardIcon : ArrowDownwardIcon;
    return (
      <div className={className}>
        <button
          type="button"
          className={"lib-sortBtn" + (on ? " is-on" : "")}
          aria-pressed={on}
          onClick={this.sortChange.bind(this, key)}
        >
          {this.props.t(SORTS[key].label)}
          {on && <Arrow />}
        </button>
      </div>
    );
  }

  renderList(rows, emptyText) {
    return (
      <div className="lib-list">
        <div className="lib-row lib-head">
          <div className="lib-slot"></div>
          {this.sortHeader("name", "lib-label")}
          {this.sortHeader("uploaded", "lib-date")}
          {this.sortHeader("duration", "lib-dur")}
          {this.sortHeader("status", "lib-status")}
          <div className="lib-more"></div>
        </div>
        {rows.map((r) => this.renderListRow(r))}
        {emptyText && <div className="lib-note lib-noRows">{emptyText}</div>}
      </div>
    );
  }

  renderCard(v, detailed) {
    let { t } = this.props;
    let st = STATUS[v.status] || STATUS.PROCESSING;
    let done = v.status === "PROCESSED";
    return (
      <Link key={v.project_id} to={this.videoUrl(v)} className="lib-card noUnderline">
        <div className="lib-cardThumb">
          {done && this.thumbnail(v)}
          {!done && (
            <div className="lib-badge">
              <st.Icon />
              {t(st.label)}
            </div>
          )}
          {v.duration && <div className="lib-durBadge">{this.formatDuration(v.duration)}</div>}
          <button type="button" className="lib-imgBtn" title={t("More")} onClick={this.menuClick.bind(this, v)}>
            <MoreVertIcon />
          </button>
        </div>
        <div className="lib-cardName" title={v.project_name}>
          {v.project_name}
        </div>
        {detailed && (
          <div className="lib-cardMeta">
            {this.formatDate(v.created_time_z)}
            <span className={st.cls}>{t(st.label)}</span>
          </div>
        )}
      </Link>
    );
  }

  renderGrid(rows, detailed, emptyText) {
    let { t } = this.props;
    // consecutive videos of the same folder share one grid
    let groups = [];
    for (let r of rows) {
      let last = groups[groups.length - 1];
      if (r.type !== "video") groups.push(r);
      else if (last && last.type === "grid" && last.parent === r.parent) last.videos.push(r.video);
      else groups.push({ type: "grid", parent: r.parent, depth: r.depth, videos: [r.video] });
    }

    return (
      <div className="lib-groups">
        {groups.map((g) => {
          let indent = { marginLeft: g.depth * INDENT };
          if (g.type === "folder") {
            return (
              <div
                key={"f:" + g.path}
                className="lib-folderTile"
                style={indent}
                role="button"
                tabIndex={0}
                aria-expanded={g.open}
                onClick={this.toggleFolder.bind(this, g.path)}
                onKeyDown={this.folderKeyDown.bind(this, g.path)}
              >
                {g.open ? <ExpandMoreIcon className="lib-caret" /> : <NavigateNextIcon className="lib-caret" />}
                {this.folderIcon(g.open)}
                <div className="lib-label" title={g.path}>
                  {baseName(g.path)}
                </div>
                {this.folderMore(g.path)}
              </div>
            );
          }
          if (g.type === "grid") {
            return (
              <div
                key={"g:" + g.videos[0].project_id}
                className={"lib-grid" + (detailed ? " is-detailed" : "")}
                style={indent}
              >
                {g.videos.map((v) => this.renderCard(v, detailed))}
              </div>
            );
          }
          return (
            <div key={g.type + ":" + g.parent} className="lib-note lib-groupNote" style={indent}>
              {g.type === "loading" ? <CircularProgress size={14} /> : t("No videos")}
            </div>
          );
        })}
        {emptyText && <div className="lib-note lib-noTiles">{emptyText}</div>}
      </div>
    );
  }

  thumbnailChange(e) {
    let file = e.target.files[0];
    var formData = new FormData();
    formData.append("image", file);

    axios
      .post("/video_project/" + this.state.menuVid + "/thumbnail", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        //onUploadProgress: this.onUploadProgress,
      })
      .then(
        function (response) {
          this.tvideo.current.poster = response.data.thumbnail;
          this.tvideo.current.src = this.tvideo.current.src;
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
        }.bind(this)
      );
  }

  setThumbnail() {
    let canvas = document.createElement("canvas");
    canvas.width = this.tvideo.current.videoWidth;
    canvas.height = this.tvideo.current.videoHeight;
    let context = canvas.getContext("2d");
    context.drawImage(this.tvideo.current, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      var formData = new FormData();
      formData.append("image", blob);

      axios
        .post("/video_project/" + this.state.menuVid + "/thumbnail", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          //onUploadProgress: this.onUploadProgress,
        })
        .then(
          function (response) {
            this.tvideo.current.poster = response.data.thumbnail;
            this.tvideo.current.src = this.tvideo.current.src;
          }.bind(this)
        )
        .catch(
          function (error) {
            console.log(error);
          }.bind(this)
        );
    });
  }

  onDrop(files) {
    if (files.length > 0) {
      let query = new URLSearchParams(this.props.location.search);
      let folder = query.get("folder")?query.get("folder") :"";
      for (let f of files) {
        let fs = [...folder.split("/"), ...(f.path || f.name).split("/")];
        fs = fs.filter(v => v !== "");
        fs.pop();
        f.folder = fs.join("/");
      }
      this.props.onDrop(files);
    }
  }

  // focus the first field once the dialog has opened: in StrictMode (dev) the focus trap takes autoFocus back
  focusDialog = () => this.dlgInput.current && this.dlgInput.current.focus();

  // compact form dialog, as the Transcript's edit popover: overline head, fields, Cancel / Save; Enter saves, Esc cancels
  formDialog({ open, onClose, title, meta, onSubmit, submitLabel, wide, children }) {
    let { t } = this.props;
    return (
      <Dialog
        className="lib-dialog"
        open={open}
        onClose={onClose}
        aria-labelledby="lib-dlg-title"
        TransitionProps={{ onEntered: this.focusDialog }}
        PaperProps={{ className: "lib-dlgPaper" + (wide ? " lib-dlgPaper--wide" : ""), elevation: 0 }}
      >
        <form className="lib-dlgForm" onSubmit={onSubmit}>
          <div className="lib-dlgHead">
            <span id="lib-dlg-title" className="lib-overline">{title}</span>
            {meta && <span className="lib-dlgMeta">{meta}</span>}
          </div>
          {children}
          <div className="lib-dlgBtns">
            <Button className="Button lib-dlgBtn" size="small" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button type="submit" className="Button ButtonPrimary lib-dlgBtn" size="small">
              {submitLabel}
            </Button>
          </div>
        </form>
      </Dialog>
    );
  }

  render() {
    let { t } = this.props;
    let view = this.state.view;
    let rows = this.buildRows();
    let emptyText =
      rows.length === 0 && !this.state.loading
        ? t(this.state.filter !== "" ? "No results found" : "No videos")
        : null;
    let title = this.state.folder ? this.state.folder.split("/").join(" / ") : t("Library");

    return (<>
      <UserBar title="" docTitle={title} wide themed></UserBar>
      <Dropzone
        ref={this.dropzoneRef}
        onDropAccepted={this.onDrop}
        accept={"video/*,.mkv"}
        maxSize={5368706371}
        noClick
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
      <div {...getRootProps({ className: "libraryPage" + (isDragActive ? " is-dragging" : "") })}>
        <input {...getInputProps()} />
      <WideContainer className="lib-body">
        {this.renderTree()}

        <div className="lib-main">
          <div className="lib-toolbar">
            <div className="lib-title">
              <span className="lib-titleText" title={title}>{title}</span>
              {this.state.loading && <CircularProgress size={16} />}
            </div>
            <TextField
              className="lib-filter"
              name="search"
              variant="outlined"
              size="small"
              placeholder={t("Filter by name")}
              value={this.state.filter}
              onChange={this.filterChange.bind(this)}
              type="search"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }
              }}
            />
            <div className="lib-views">
              {VIEWS.map(({ view: v, label, Icon }) => (
                <IconButton
                  key={v}
                  className={"lib-viewBtn" + (view === v ? " is-selected" : "")}
                  title={t(label)}
                  aria-pressed={view === v}
                  onClick={this.viewChange.bind(this, v)}
                >
                  <Icon />
                </IconButton>
              ))}
            </div>
          </div>

          {view === "list"
            ? this.renderList(rows, emptyText)
            : this.renderGrid(rows, view === "detailed", emptyText)}

          <div className="lib-hint">
            <Trans>Drop video files anywhere to upload &middot; <Link to="/account/settings">settings</Link></Trans>
          </div>
        </div>
      </WideContainer>
      </div>
        )}
      </Dropzone>
      <Menu
        className="lib-menu"
        anchorEl={this.state.menuAnchor}
        open={Boolean(this.state.menuAnchor)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => {
          this.setState({ menuAnchor: null });
        }}
      >
        <MenuItem
          onClick={() => {
            this.setState({ edit_open: true, menuAnchor: null });
          }}
        >
          <Trans>Edit informations</Trans>
        </MenuItem>
        <MenuItem onClick={this.deleteProject.bind(this)}>
          <Trans>Delete</Trans>
        </MenuItem>
      </Menu>
      <Menu
        className="lib-menu"
        anchorEl={this.state.folderAnchor}
        open={Boolean(this.state.folderAnchor)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => {
          this.setState({ folderAnchor: null });
        }}
      >
        <MenuItem
          onClick={() => {
            this.setState({ edit_folder_open: true, folderAnchor: null });
          }}
        >
          <Trans>Rename</Trans>
        </MenuItem>
        <MenuItem
          onClick={this.deleteFolder.bind(this)
          }
        >
          <Trans>Delete</Trans>
        </MenuItem>
      </Menu>
      {this.formDialog({
        open: this.state.edit_open,
        onClose: () => this.setState({ edit_open: false }),
        title: t("Video Details"),
        meta: this.state.edit_video && this.formatDuration(this.state.edit_video.duration),
        onSubmit: this.updateProject.bind(this),
        submitLabel: t("Save"),
        wide: true,
        children: (<>
          <div className="lib-dlgField">
            <label className="lib-dlgLabel" htmlFor="lib-dlg-name">
              {t("Name")}
            </label>
            <TextField
              id="lib-dlg-name"
              inputRef={this.dlgInput}
              size="small"
              name="edit_name"
              fullWidth
              value={this.state.edit_name}
              onChange={this.handleEdit.bind(this)}
            />
          </div>
          <div className="lib-dlgField">
            <label className="lib-dlgLabel" htmlFor="lib-dlg-folder">
              {t("Folder")}
            </label>
            <Autocomplete
              id="lib-dlg-folder"
              options={this.state.folders}
              freeSolo
              size="small"
              fullWidth
              getOptionLabel={(option) => option}
              inputValue={this.state.edit_folder}
              value={this.state.edit_folder}
              onInputChange={(e, v) => {
                this.setState({ edit_folder: v });
              }}
              slotProps={{ paper: { className: "lib-menu" } }}
              renderInput={(params) => <TextField {...params} size="small" name="edit_folder" />}
            />
          </div>
          <div className="lib-dlgRow lib-dlgThumbHead">
            <span className="lib-dlgLabel">{t("Thumbnail")}</span>
            <input
              accept="image/*"
              type="file"
              id="upload-input"
              hidden
              onChange={this.thumbnailChange.bind(this)}
            />
            <label htmlFor="upload-input">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                component="span"
              >
                {t("Upload Thumbnail")}
              </Button>
            </label>
          </div>
          <video
            preload="auto"
            poster={
              this.user.user_folder +
              "/" +
              this.state.edit_video.project_folder +
              "/video_lowres_thumbnail.jpg"
            }
            controls
            className="lib-dlgVideo"
            ref={this.tvideo}
            crossOrigin="use-credentials"
            src={
              this.user.user_folder +
              "/" +
              this.state.edit_video.project_folder +
              "/video_lowres.mp4"
            }
          />
          <div className="lib-dlgRow">
            <ButtonGroup
              color="primary"
              size="small"
              aria-label="outlined primary button group"
            >
              <Button
                onClick={() => {
                  this.tvideo.current.currentTime =
                    this.tvideo.current.currentTime - 1;
                }}
              >
                -1 sec
              </Button>
              <Button
                onClick={() => {
                  this.tvideo.current.currentTime =
                    this.tvideo.current.currentTime - 1 / 25;
                }}
              >
                -1 Frame
              </Button>
              <Button
                onClick={() => {
                  if (this.tvideo.current.paused) this.tvideo.current.play();
                  else this.tvideo.current.pause();
                }}
              >
                <PlayArrowIcon />
              </Button>
              <Button
                onClick={() => {
                  this.tvideo.current.currentTime =
                    this.tvideo.current.currentTime + 1 / 25;
                }}
              >
                +1 Frame
              </Button>
              <Button
                onClick={() => {
                  this.tvideo.current.currentTime =
                    this.tvideo.current.currentTime + 1;
                }}
              >
                +1 sec
              </Button>
            </ButtonGroup>
            <Button
              onClick={this.setThumbnail.bind(this)}
              color="primary"
              size="small"
              variant="outlined"
            >
              {t("Set frame as thumbnail")}
            </Button>
          </div>
        </>),
      })}
      {this.formDialog({
        open: this.state.edit_folder_open,
        onClose: () => this.setState({ edit_folder_open: false }),
        title: t("Rename Folder"),
        onSubmit: this.renameFolder.bind(this),
        submitLabel: t("Save"),
        children: (
          <TextField
            inputRef={this.dlgInput}
            size="small"
            name="folder_new"
            fullWidth
            value={this.state.folder_new}
            onChange={this.handleEdit.bind(this)}
            slotProps={{ htmlInput: { "aria-label": t("Folder Name") } }}
          />
        ),
      })}
      {this.formDialog({
        open: this.state.create_folder_open,
        onClose: () => this.setState({ create_folder_open: false }),
        title: t("Create Folder"),
        onSubmit: this.createFolder.bind(this),
        submitLabel: t("Create"),
        children: (
          <TextField
            inputRef={this.dlgInput}
            size="small"
            name="folder_new"
            fullWidth
            placeholder={t("Folder Name")}
            value={this.state.folder_new}
            onChange={this.handleEdit.bind(this)}
            slotProps={{ htmlInput: { "aria-label": t("Folder Name") } }}
          />
        ),
      })}
    </>);
  }
}

export default withTranslation()(Index);
